/**
 * 🚀 ESPACE LIVREUR V2 - "Le Vaisseau de Livraison"
 * Design harmonisé avec le Cockpit Admin et le SAS
 * Le livreur est un pilote de petit vaisseau de ravitaillement
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket, Package, MapPin, Phone, Navigation, CheckCircle, 
  Clock, LogOut, User, Trophy, History, ChevronRight,
  RefreshCw, AlertCircle, Star, Zap, Gift, Fuel,
  ChevronDown, ExternalLink, CreditCard, Banknote, X,
  Radio, Shield, Target, Compass, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../services/api';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

// Statuts de mission avec thème spatial
const MISSION_STATUS = {
  pending: { label: 'En attente', color: 'yellow', icon: Clock, action: 'Accepter la mission' },
  confirmed: { label: 'Confirmée', color: 'cyan', icon: Target, action: 'Préparer' },
  preparing: { label: 'En préparation', color: 'orange', icon: Package, action: 'Décollage' },
  delivering: { label: 'En vol', color: 'blue', icon: Rocket, action: 'Mission accomplie' },
  delivered: { label: 'Livrée', color: 'green', icon: CheckCircle, action: null },
};

// Étoiles animées en fond (positions pré-générées)
const STAR_POSITIONS = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  left: (i * 17 + 13) % 100,
  top: (i * 23 + 7) % 100,
  duration: 2 + (i % 5),
  delay: (i % 10) / 5,
}));

const StarField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {STAR_POSITIONS.map((star) => (
      <motion.div
        key={star.id}
        className="absolute w-1 h-1 bg-white rounded-full"
        style={{
          left: `${star.left}%`,
          top: `${star.top}%`,
        }}
        animate={{
          opacity: [0.2, 0.8, 0.2],
          scale: [1, 1.5, 1],
        }}
        transition={{
          duration: star.duration,
          repeat: Infinity,
          delay: star.delay,
        }}
      />
    ))}
  </div>
);

// Carte de mission (livraison)
const MissionCard = ({ order, onUpdateStatus, onCall, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  const status = MISSION_STATUS[order.status] || MISSION_STATUS.pending;
  const StatusIcon = status.icon;

  // Extraire les données
  const clientName = order.prenom && order.nom 
    ? `${order.prenom} ${order.nom}` 
    : order.customerName || 'Client';
  const clientPhone = order.telephone || order.customerPhone || '';
  const clientAddress = order.adresse || order.address || '';
  const orderDetails = order.commentaire || order.orderDetails || '';
  
  // Extraire le montant
  const amountMatch = orderDetails.match(/Montant estimé: (\d+)/);
  const amount = amountMatch ? amountMatch[1] : order.totalAmount || '?';

  // Extraire le créneau
  const slotMatch = orderDetails.match(/🕐 Créneau: ([^\n]+)/);
  const timeSlot = slotMatch ? slotMatch[1] : 'ASAP';

  const getStatusGradient = () => {
    switch(status.color) {
      case 'yellow': return 'from-yellow-500/20 to-orange-500/10';
      case 'cyan': return 'from-cyan-500/20 to-blue-500/10';
      case 'orange': return 'from-orange-500/20 to-red-500/10';
      case 'blue': return 'from-blue-500/20 to-purple-500/10';
      case 'green': return 'from-green-500/20 to-cyan-500/10';
      default: return 'from-gray-500/20 to-gray-500/10';
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`rounded-2xl overflow-hidden border border-${status.color}-500/30`}
      style={{
        background: `linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(20,20,40,0.8) 100%)`,
        boxShadow: `0 0 20px rgba(var(--${status.color}-rgb), 0.2)`
      }}
    >
      {/* Header de la mission */}
      <div 
        className={`p-4 cursor-pointer bg-gradient-to-r ${getStatusGradient()}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {/* Icône statut animée */}
          <motion.div 
            animate={{ 
              boxShadow: order.status === 'delivering' 
                ? ['0 0 10px #3b82f6', '0 0 30px #3b82f6', '0 0 10px #3b82f6']
                : 'none'
            }}
            transition={{ duration: 1, repeat: Infinity }}
            className={`w-12 h-12 rounded-xl bg-${status.color}-500/20 border border-${status.color}-500/50 flex items-center justify-center`}
          >
            <StatusIcon className={`w-6 h-6 text-${status.color}-400`} />
          </motion.div>

          {/* Infos client */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-bold truncate">{clientName}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold bg-${status.color}-500/30 text-${status.color}-400 border border-${status.color}-500/50`}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
              <Clock className="w-3 h-3" />
              <span>{timeSlot}</span>
              <span className="text-cyan-400 font-bold ml-2">{amount}€</span>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={(e) => { e.stopPropagation(); onCall?.(clientPhone); }}
              className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors border border-green-500/30"
            >
              <Phone className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={(e) => { e.stopPropagation(); onNavigate?.(clientAddress); }}
              className="p-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-colors border border-cyan-500/30"
            >
              <Navigation className="w-4 h-4" />
            </motion.button>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {/* Détails expandés */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {/* Adresse */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-white/5">
                <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-400 text-xs mb-1">Destination</p>
                  <p className="text-white">{clientAddress || 'Pas d&apos;adresse disponible'}</p>
                </div>
              </div>

              {/* Téléphone */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                <Phone className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-gray-400 text-xs mb-1">Contact</p>
                  <p className="text-white font-mono text-lg">{clientPhone || 'Non renseigné'}</p>
                </div>
              </div>

              {/* Commande */}
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                  <Package className="w-4 h-4" /> Cargaison
                </p>
                <p className="text-white whitespace-pre-line text-sm">{orderDetails}</p>
              </div>

              {/* Bouton d'action principal */}
              {status.action && order.status !== 'delivered' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (order.status === 'delivering') {
                      // Ouvrir le modal de paiement
                      onUpdateStatus(order.id, 'payment_modal', amount);
                    } else {
                      const nextStatus = {
                        pending: 'confirmed',
                        confirmed: 'preparing',
                        preparing: 'delivering',
                      }[order.status];
                      onUpdateStatus(order.id, nextStatus);
                    }
                  }}
                  className={`w-full py-4 rounded-xl font-bold text-black flex items-center justify-center gap-2
                    ${order.status === 'pending' ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                      order.status === 'confirmed' ? 'bg-gradient-to-r from-cyan-400 to-blue-500' :
                      order.status === 'preparing' ? 'bg-gradient-to-r from-orange-400 to-red-500' :
                      'bg-gradient-to-r from-green-400 to-cyan-500'
                    }`}
                >
                  {order.status === 'pending' && <Target className="w-5 h-5" />}
                  {order.status === 'confirmed' && <Package className="w-5 h-5" />}
                  {order.status === 'preparing' && <Rocket className="w-5 h-5" />}
                  {order.status === 'delivering' && <CheckCircle className="w-5 h-5" />}
                  {status.action}
                </motion.button>
              )}

              {order.status === 'delivered' && (
                <div className="flex items-center justify-center gap-2 py-4 text-green-400">
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-bold">Mission accomplie !</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Modal de confirmation de paiement
const PaymentModal = ({ isOpen, onClose, onConfirm, amount }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm rounded-3xl p-6"
          style={{
            background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 100%)',
            border: '2px solid #00FFFF',
            boxShadow: '0 0 40px rgba(0, 255, 255, 0.3)'
          }}
        >
          <div className="text-center mb-6">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5 }}
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500 to-green-500 flex items-center justify-center"
            >
              <CreditCard className="w-10 h-10 text-white" />
            </motion.div>
            <h3 className="text-2xl font-bold text-white mb-2">Mode de paiement</h3>
            <p className="text-cyan-400 text-lg font-bold">{amount}€</p>
          </div>

          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onConfirm('card')}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold flex items-center justify-center gap-3"
            >
              <CreditCard className="w-6 h-6" />
              Carte bancaire
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onConfirm('cash')}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-cyan-500 text-white font-bold flex items-center justify-center gap-3"
            >
              <Banknote className="w-6 h-6" />
              Espèces
            </motion.button>
          </div>

          <button
            onClick={onClose}
            className="w-full mt-4 py-3 rounded-xl bg-white/10 text-gray-400 hover:bg-white/20 transition-colors"
          >
            Annuler
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Composant principal
export default function DriverPageV2() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [driver, setDriver] = useState(null);
  const [code, setCode] = useState(['', '', '', '']);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('missions');
  const [paymentModal, setPaymentModal] = useState({ show: false, orderId: null, amount: 0 });
  const [history, setHistory] = useState([]);

  // Charger le livreur sauvegardé
  useEffect(() => {
    const savedDriver = localStorage.getItem('lpe_driver');
    if (savedDriver) {
      const driverData = JSON.parse(savedDriver);
      setDriver(driverData);
      setIsLoggedIn(true);
      loadOrders(driverData.id);
    }
  }, []);

  // Rafraîchir les commandes toutes les 30s
  useEffect(() => {
    if (isLoggedIn && driver) {
      const interval = setInterval(() => loadOrders(driver.id), 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, driver]);

  const loadOrders = useCallback(async (driverId) => {
    try {
      const data = await api.getDriverOrders?.(driverId) || { orders: [] };
      const newOrders = data.orders || [];
      
      // Notification si nouvelle commande
      if (newOrders.length > orders.length && orders.length > 0) {
        toast.success('🚀 Nouvelle mission assignée !');
        if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
      }
      
      setOrders(newOrders);
    } catch (error) {
      console.error('Erreur chargement:', error);
      // Fallback: charger les demandes de livraison en attente
      try {
        const deliveries = await api.getDeliveryRequests?.();
        const pending = (deliveries || []).filter(d => 
          d.status === 'pending' || d.status === 'confirmed' || d.status === 'preparing' || d.status === 'delivering'
        );
        setOrders(pending);
      } catch (e) {
        console.error('Erreur fallback:', e);
      }
    }
  }, [orders.length]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await api.getDriverHistory?.(driver?.id);
      setHistory(data?.deliveries || []);
    } catch (error) {
      console.error('Erreur historique:', error);
      // Fallback
      const deliveries = await api.getDeliveryRequests?.();
      setHistory((deliveries || []).filter(d => d.status === 'delivered'));
    }
  }, [driver]);

  useEffect(() => {
    if (activeTab === 'history' && driver) {
      loadHistory();
    }
  }, [activeTab, driver, loadHistory]);

  const handleLogin = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 4) {
      setError('Code à 4 chiffres requis');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await api.driverLogin?.(fullCode);
      if (data?.driver) {
        localStorage.setItem('lpe_driver', JSON.stringify(data.driver));
        setDriver(data.driver);
        setIsLoggedIn(true);
        loadOrders(data.driver.id);
        toast.success(`🚀 Bienvenue à bord, ${data.driver.name} !`);
      } else {
        throw new Error('Connexion échouée');
      }
    } catch (error) {
      // Mode démo si pas de backend
      if (fullCode === '1234') {
        const demoDriver = { id: 'demo', name: 'Pilote Démo', score: 100, totalDeliveries: 0 };
        localStorage.setItem('lpe_driver', JSON.stringify(demoDriver));
        setDriver(demoDriver);
        setIsLoggedIn(true);
        loadOrders('demo');
        toast.success('🚀 Mode démo activé !');
      } else {
        setError('Code invalide');
        setCode(['', '', '', '']);
        toast.error('Code invalide');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lpe_driver');
    setIsLoggedIn(false);
    setDriver(null);
    setOrders([]);
    setCode(['', '', '', '']);
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 3) {
      document.getElementById(`pilot-code-${index + 1}`)?.focus();
    }
    if (newCode.every(d => d !== '') && index === 3) {
      setTimeout(() => handleLogin(), 100);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`pilot-code-${index - 1}`)?.focus();
    }
  };

  const handleUpdateStatus = async (orderId, newStatus, amount = null) => {
    try {
      if (newStatus === 'payment_modal') {
        setPaymentModal({ show: true, orderId, amount: amount || 0 });
        return;
      }
      
      if (newStatus === 'delivered' && amount) {
        await api.updateDeliveryStatus?.(orderId, newStatus);
        toast.success(`🎉 Mission accomplie ! Payé par ${amount === 'card' ? 'CB' : 'espèces'}`);
      } else {
        await api.updateDeliveryStatus?.(orderId, newStatus);
        const statusMessages = {
          confirmed: '✅ Mission acceptée !',
          preparing: '📦 Cargaison en préparation...',
          delivering: '🚀 Décollage ! En route vers la destination.',
        };
        toast.success(statusMessages[newStatus] || 'Statut mis à jour');
      }
      
      setPaymentModal({ show: false, orderId: null, amount: 0 });
      loadOrders(driver?.id);
    } catch (error) {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleCall = (phone) => {
    if (phone) window.location.href = `tel:${phone}`;
    else toast.error('Numéro non disponible');
  };

  const handleNavigate = (address) => {
    if (address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
    } else {
      toast.error('Adresse non disponible');
    }
  };

  // Page de connexion
  if (!isLoggedIn) {
    return (
      <div 
        className="min-h-screen overflow-x-hidden overflow-y-auto flex items-center justify-center p-4 relative"
        style={{
          background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 30%, #0d1b2a 70%, #0a0015 100%)',
        }}
      >
        <StarField />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md relative z-10"
        >
          {/* Logo vaisseau */}
          <div className="text-center mb-8">
            <motion.div
              animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="inline-block mb-4"
            >
              <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border-2 border-cyan-500/50 flex items-center justify-center"
                style={{ boxShadow: '0 0 50px rgba(0,255,255,0.3)' }}
              >
                <Rocket className="w-16 h-16 text-cyan-400" />
              </div>
            </motion.div>
            
            <h1 className="text-3xl font-bold text-white mb-2">Vaisseau de Livraison</h1>
            <p className="text-cyan-400">Identification du pilote requise</p>
          </div>

          {/* Card de connexion */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="backdrop-blur-xl rounded-3xl p-8"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, rgba(255, 0, 255, 0.05) 100%)',
              border: '2px solid rgba(0, 255, 255, 0.3)',
              boxShadow: '0 0 50px rgba(0, 255, 255, 0.2)'
            }}
          >
            <h2 className="text-xl font-bold text-white text-center mb-6 flex items-center justify-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              Code d&apos;accès pilote
            </h2>

            <div className="flex gap-3 justify-center mb-6">
              {code.map((digit, index) => (
                <motion.input
                  key={index}
                  id={`pilot-code-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  whileFocus={{ scale: 1.1 }}
                  className="w-14 h-14 text-center text-2xl font-bold rounded-xl bg-black/50 border-2 border-cyan-500/50 text-cyan-400 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-all"
                  style={{ textShadow: '0 0 10px rgba(0,255,255,0.5)' }}
                />
              ))}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/50 flex items-center gap-2 text-red-400"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            <Button
              onClick={handleLogin}
              disabled={loading || code.some(d => !d)}
              className="w-full py-4 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold rounded-xl"
            >
              {loading ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                  <RefreshCw className="w-5 h-5" />
                </motion.div>
              ) : (
                <>
                  <Rocket className="w-5 h-5 mr-2" />
                  Embarquer
                </>
              )}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Dashboard principal
  const activeMissions = orders.filter(o => o.status !== 'delivered');
  const completedToday = orders.filter(o => o.status === 'delivered').length;

  return (
    <div 
      className="min-h-screen overflow-x-hidden overflow-y-auto relative pb-24"
      style={{
        background: 'linear-gradient(135deg, #0a0015 0%, #1a0033 30%, #0d1b2a 70%, #0a0015 100%)',
      }}
    >
      <StarField />

      {/* Header du vaisseau */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-4 border-b border-cyan-500/20"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, transparent 100%)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div 
              animate={{ 
                boxShadow: ['0 0 10px #0ff', '0 0 30px #0ff', '0 0 10px #0ff']
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center border border-cyan-400/50"
            >
              <Rocket className="w-7 h-7 text-white" />
            </motion.div>
            <div>
              <h1 className="text-xl font-bold text-white">{driver?.name}</h1>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-yellow-400">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-sm font-semibold">{driver?.score || 100}</span>
                </div>
                <span className="text-gray-600">•</span>
                <span className="text-cyan-400 text-sm">{driver?.totalDeliveries || 0} missions</span>
              </div>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </motion.div>

      {/* Stats du vaisseau */}
      <div className="relative z-10 px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-2xl text-center border border-green-500/30"
            style={{ background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15) 0%, rgba(0, 255, 136, 0.05) 100%)' }}
          >
            <motion.div 
              animate={{ scale: activeMissions.length > 0 ? [1, 1.1, 1] : 1 }}
              transition={{ duration: 1, repeat: Infinity }}
              className="text-3xl font-bold text-green-400"
            >
              {activeMissions.length}
            </motion.div>
            <p className="text-green-300 text-xs mt-1 flex items-center justify-center gap-1">
              <Radio className="w-3 h-3" /> En vol
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-4 rounded-2xl text-center border border-cyan-500/30"
            style={{ background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.15) 0%, rgba(0, 255, 255, 0.05) 100%)' }}
          >
            <p className="text-3xl font-bold text-cyan-400">{completedToday}</p>
            <p className="text-cyan-300 text-xs mt-1 flex items-center justify-center gap-1">
              <CheckCircle className="w-3 h-3" /> Ce soir
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-4 rounded-2xl text-center border border-yellow-500/30"
            style={{ background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.05) 100%)' }}
          >
            <p className="text-3xl font-bold text-yellow-400">
              {orders.reduce((sum, o) => {
                const match = (o.commentaire || '').match(/Montant estimé: (\d+)/);
                return sum + (match ? parseInt(match[1]) : (o.totalAmount || 0));
              }, 0)}€
            </p>
            <p className="text-yellow-300 text-xs mt-1 flex items-center justify-center gap-1">
              <Fuel className="w-3 h-3" /> Carburant
            </p>
          </motion.div>
        </div>
      </div>

      {/* Tabs */}
      <div className="relative z-10 px-4 mb-4">
        <div className="flex gap-2 p-1 rounded-2xl bg-black/30 border border-white/10">
          <button
            onClick={() => setActiveTab('missions')}
            className={`flex-1 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'missions'
                ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white'
                : 'text-gray-400 hover:bg-white/10'
            }`}
          >
            <Rocket className="w-5 h-5" />
            Missions
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'history'
                ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white'
                : 'text-gray-400 hover:bg-white/10'
            }`}
          >
            <History className="w-5 h-5" />
            Journal
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="relative z-10 px-4 pb-6">
        {activeTab === 'missions' && (
          <div className="space-y-4">
            {/* Bouton rafraîchir */}
            <motion.button
              onClick={() => loadOrders(driver?.id)}
              whileTap={{ scale: 0.95 }}
              className="w-full py-3 rounded-xl bg-white/5 text-gray-400 hover:bg-cyan-500/10 hover:text-cyan-400 transition-all flex items-center justify-center gap-2 border border-white/10 hover:border-cyan-500/30"
            >
              <RefreshCw className="w-4 h-4" />
              Scanner les missions
            </motion.button>

            {activeMissions.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16 rounded-3xl border border-dashed border-cyan-500/30"
                style={{ background: 'linear-gradient(135deg, rgba(0,255,255,0.05) 0%, rgba(255,0,255,0.02) 100%)' }}
              >
                <motion.div
                  animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <Rocket className="w-20 h-20 mx-auto text-cyan-500/30" />
                </motion.div>
                <h3 className="text-xl font-bold text-white mt-6 mb-2">Vaisseau en attente</h3>
                <p className="text-gray-400">Aucune mission assignée pour le moment</p>
                <p className="text-cyan-400 text-sm mt-2">Restez à l&apos;écoute du radar 📡</p>
              </motion.div>
            ) : (
              activeMissions.map((order) => (
                <MissionCard
                  key={order.id}
                  order={order}
                  onUpdateStatus={handleUpdateStatus}
                  onCall={handleCall}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {history.length === 0 && orders.filter(o => o.status === 'delivered').length === 0 ? (
              <div className="text-center py-16 rounded-3xl border border-dashed border-gray-700">
                <History className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Aucune mission terminée</p>
              </div>
            ) : (
              [...history, ...orders.filter(o => o.status === 'delivered')].map((order) => (
                <MissionCard
                  key={order.id}
                  order={order}
                  onCall={handleCall}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal de paiement */}
      <PaymentModal
        isOpen={paymentModal.show}
        onClose={() => setPaymentModal({ show: false, orderId: null, amount: 0 })}
        onConfirm={(method) => handleUpdateStatus(paymentModal.orderId, 'delivered', method)}
        amount={paymentModal.amount}
      />
    </div>
  );
}
